import React, { useCallback, useEffect, useState } from 'react';

import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { DialogModal, ItemIcon, MenuItem, ModalBox, Table } from 'components';
import { useIsMobile } from 'hooks';

import { InviteUserForm } from './InviteUserForm';
import { userColumns } from './MembersColumns';
import { UserRightsEditForm } from './UserRightsEditForm';

import { useSelector, useTranslation } from 'third-party';

import { PersisTables } from 'constants/common';

import { useUser } from 'modules/user';

import { appSelectors, userSelectors } from 'selectors';

import { FilterValue } from 'components/Table/types';

import {
  extractFiltersFromSavedColumns,
  mergeSavedColumnsWithExisting,
} from 'utils/app';
import {
  getFromStorage,
  removeFromStorage,
  saveInStorage,
} from 'utils/storage';

import { APISortOrder, APIUser } from 'types/api';

export const MembersList = () => {
  const { t } = useTranslation();

  const [selectedItem, setSelectedItem] = useState<APIUser | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const {
    loadUsers,
    loadMoreUsers,
    updateUserFilters,
    deleteUser,
    clearUserList,
    clearUserFilters,
  } = useUser();
  const isMobile = useIsMobile();

  const [loadedUserSettings, setLoadedUserSettings] = useState(false);
  const tenantId = useSelector(appSelectors.tenantId);
  const items = useSelector(userSelectors.userList);
  const userListLoaded = useSelector(userSelectors.usersListLoaded);
  const allUsersLoaded = useSelector(userSelectors.allUsersLoaded);
  const userFilters = useSelector(userSelectors.userFilters);
  const loadUsersPending = useSelector(userSelectors.loadUsersPending);
  const loadMoreUsersPending = useSelector(userSelectors.loadMoreUsersPending);
  const [columns, setColumns] = useState(userColumns(tenantId));

  useEffect(() => {
    const savedColumns = getFromStorage(
      PersisTables.Members,
      userColumns(tenantId),
    );
    if (!savedColumns) {
      setLoadedUserSettings(true);
      return;
    }

    updateUserFilters(extractFiltersFromSavedColumns(savedColumns));
    setColumns(mergeSavedColumnsWithExisting(savedColumns, columns));
    setLoadedUserSettings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    if (tenantId && loadedUserSettings) {
      loadUsers();
    }
  }, [userFilters, tenantId, loadedUserSettings, loadUsers]);

  useEffect(() => {
    return () => {
      clearUserList();
      clearUserFilters();
    };
  }, [clearUserFilters, clearUserList, tenantId]);

  const onColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setColumns(prevColumns =>
        prevColumns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filter: value };
          }
          return column;
        }),
      );
      updateUserFilters({ ...userFilters, [columnId]: value });
    },
    [userFilters, columns, updateUserFilters],
  );

  const onAdvancedFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filterValue: value };
          }
          return column;
        }),
      );

      updateUserFilters({ ...userFilters, [columnId]: value });
    },
    [userFilters, columns, updateUserFilters],
  );

  const onFilterReset = useCallback(() => {
    updateUserFilters({});
    setColumns(it =>
      it.map(column => ({
        ...column,
        filter: undefined,
        filterValue: undefined,
      })),
    );
  }, [updateUserFilters]);

  const onColumnVisibilityChanged = useCallback(
    (columnId: string, value: boolean) => {
      setColumns(it =>
        it.map(column => {
          if (column.dataId === columnId) {
            return { ...column, hidden: !value };
          }
          return column;
        }),
      );
    },
    [],
  );

  const rowOptionsMenu = useCallback(
    (item: APIUser, onRowMenuClose: () => void): React.ReactElement[] => [
      <MenuItem
        key={'delete'}
        onClick={() => {
          setSelectedItem(item);
          onRowMenuClose();
        }}
      >
        <ItemIcon icon={DeleteOutlineOutlinedIcon} />
        {t('membersList.removeFromOrganization')}
      </MenuItem>,
      <MenuItem
        key={'edit'}
        onClick={() => {
          setIsEditModalOpen(true);
          setSelectedItem(item);
          onRowMenuClose();
        }}
      >
        <ItemIcon icon={EditOutlinedIcon} />
        {t('table.edit')}
      </MenuItem>,
    ],
    [t],
  );

  const onEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(null);
  }, []);

  const onInviteModalClose = () => {
    setIsInviteModalOpen(false);
  };

  const onColumnHeaderClicked = useCallback(
    (columnDataId: string) => {
      const existingSortOrder = userFilters.searchSortOrders?.find(
        order => order.sortField === columnDataId,
      );

      const newSortOrder =
        existingSortOrder && existingSortOrder.sortOrder === APISortOrder.DESC
          ? APISortOrder.ASC
          : APISortOrder.DESC;

      updateUserFilters({
        ...userFilters,
        searchSortOrders: [
          {
            sortField: columnDataId,
            sortOrder: newSortOrder,
          },
        ],
      });
    },
    [userFilters, updateUserFilters],
  );

  return (
    <>
      <Table<APIUser>
        draggable
        header={t('membersList.header')}
        subHeader={t('membersList.subHeader')}
        onSaveTableSettings={settings => {
          saveInStorage(PersisTables.Members, settings);
        }}
        onResetTableSettings={() => {
          setColumns(userColumns(tenantId));
          updateUserFilters({});
          removeFromStorage(PersisTables.Members);
        }}
        onColumnSizeChanged={(it, width) => {
          setColumns(prevColumns =>
            prevColumns.map(column =>
              column.dataId === it ? { ...column, width: width } : column,
            ),
          );
        }}
        onColumnOrderChanged={setColumns}
        filterable
        allItemsLoaded={allUsersLoaded}
        infiniteScroll
        tableMinHeight="20rem"
        columns={columns}
        items={items || []}
        sortOrder={userFilters.searchSortOrders?.[0]?.sortOrder}
        rowOptionsMenu={rowOptionsMenu}
        onColumnHeaderClicked={onColumnHeaderClicked}
        onColumnFilterChange={onColumnFilterChange}
        onAdvancedFilterChange={onAdvancedFilterChange}
        onColumnVisibilityChanged={onColumnVisibilityChanged}
        loadItemsPending={loadUsersPending}
        loadMoreItemsPending={loadMoreUsersPending}
        onLoadMoreClicked={loadMoreUsers}
        onFilterReset={onFilterReset}
        bodyRowHeight="1rem"
        headerRowHeight="0.2rem"
        itemsLoaded={userListLoaded}
        onNewItemButtonClicked={() => setIsInviteModalOpen(true)}
        emptyText={t('table.empty')}
        loadMoreButtonText={t('table.loadMore')}
      />
      {isEditModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '34rem'}
          onClose={onEditModalClose}
        >
          <UserRightsEditForm
            tenantId={tenantId}
            onModalClose={onEditModalClose}
            userId={selectedItem?.id}
          />
        </ModalBox>
      )}
      {isInviteModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '34rem'}
          onClose={onInviteModalClose}
        >
          <InviteUserForm onModalClose={onInviteModalClose} />
        </ModalBox>
      )}
      <DialogModal
        onClose={() => setSelectedItem(null)}
        open={!!selectedItem && !isEditModalOpen}
        description={t('membersList.deleteConfirm')}
        handleActionClick={() => {
          if (selectedItem) {
            deleteUser(selectedItem.id);
          }
          setSelectedItem(null);
        }}
      />
    </>
  );
};
